import {
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import {
  useNavigate,
  useParams,
  useLocation
} from "react-router-dom";

import "./Stoarage.css";
import VideoPlayer from "../VideoPlayer/VideoPlayer";


// =====================================================
// API BASE
// =====================================================

const API_BASE =
  "https://td9dmwg0-8000.inc1.devtunnels.ms";


// =====================================================
// COMPONENT
// =====================================================

function Storage() {

  const navigate = useNavigate();
  const location = useLocation();

  const {
    folderId: urlFolderId
  } = useParams();


  // =====================================================
  // VIEW / FILTER STATE
  // =====================================================

  const [viewMode, setViewMode] =
    useState("grid");

  const [search, setSearch] =
    useState("");

  const [typeFilter, setTypeFilter] =
    useState("all");

  const [modifiedFilter, setModifiedFilter] =
    useState("all");


  // =====================================================
  // MENU STATE
  // =====================================================

  const [showNewMenu, setShowNewMenu] =
    useState(false);

  const [openMenu, setOpenMenu] =
    useState(null);

  const [showTypeMenu, setShowTypeMenu] =
    useState(false);

  const [showModifiedMenu, setShowModifiedMenu] =
    useState(false);


  // =====================================================
  // FOLDER STATE
  // =====================================================

  const [currentFolder, setCurrentFolder] =
    useState(null);

  const [currentFolderPublicId, setCurrentFolderPublicId] =
    useState(null);

  const [currentFolderName, setCurrentFolderName] =
    useState(null);

  const [folderPath, setFolderPath] =
    useState([]);


  // =====================================================
  // CONTENT STATE
  // =====================================================

  const [files, setFiles] =
    useState([]);

  const [loading, setLoading] =
    useState(true);


  // =====================================================
  // UPLOAD STATE
  // =====================================================

  const [uploading, setUploading] =
    useState(false);

  const [uploadProgress, setUploadProgress] =
    useState(0);

  const [uploadCurrent, setUploadCurrent] =
    useState(0);

  const [uploadTotal, setUploadTotal] =
    useState(0);

  const [uploadFileName, setUploadFileName] =
    useState("");

  const [uploadStatus, setUploadStatus] =
    useState("");

  const [uploadCompleted, setUploadCompleted] =
    useState(false);


  // =====================================================
  // VIDEO STATE
  // =====================================================

  const [selectedVideo, setSelectedVideo] =
    useState(null);


  // =====================================================
  // SHARE STATE
  // =====================================================

  const [shareModal, setShareModal] =
    useState(null);

  const [shareLoading, setShareLoading] =
    useState(false);

  const [shareData, setShareData] =
    useState(null);


  // =====================================================
  // REFS
  // =====================================================

  const fileInputRef =
    useRef(null);

  const folderInputRef =
    useRef(null);

  const csrfTokenRef =
    useRef(null);

  const csrfPromiseRef =
    useRef(null);

  const refreshPromiseRef =
    useRef(null);

  /*
   * Each loadContents() gets a unique request id.
   *
   * We DO NOT abort the contents request.
   *
   * This is important because we want:
   *
   * OPTIONS
   *   ↓
   * GET
   *   ↓
   * RESPONSE
   *
   * to complete normally.
   */
  const contentsRequestRef =
    useRef(0);


  // =====================================================
  // SAFE RESPONSE PARSER
  // =====================================================

  const parseResponseData =
    async response => {

      try {

        const text =
          await response.text();

        if (!text) {
          return {};
        }

        try {

          return JSON.parse(text);

        } catch {

          return {
            detail: text
          };

        }

      } catch {

        return {};

      }

    };


  // =====================================================
  // REQUEST WITH TIMEOUT
  //
  // FIX:
  // If caller supplies an AbortSignal, timeout and caller
  // cancellation are both respected.
  // =====================================================

  const fetchWithTimeout =
    async (
      url,
      options = {},
      timeout = 15000
    ) => {

      const timeoutController =
        new AbortController();

      const externalSignal =
        options.signal || null;


      const abortRequest =
        () => {

          if (
            !timeoutController.signal.aborted
          ) {

            timeoutController.abort();

          }

        };


      let externalAbortHandler =
        null;


      if (externalSignal) {

        if (
          externalSignal.aborted
        ) {

          timeoutController.abort();

        } else {

          externalAbortHandler =
            () => {

              timeoutController.abort();

            };

          externalSignal.addEventListener(
            "abort",
            externalAbortHandler,
            {
              once: true
            }
          );

        }

      }


      const timeoutId =
        setTimeout(
          abortRequest,
          timeout
        );


      try {

        console.log(
          "FETCH START:",
          options.method || "GET",
          url
        );


        return await fetch(
          url,
          {
            ...options,

            signal:
              timeoutController.signal
          }
        );

      } finally {

        clearTimeout(
          timeoutId
        );


        if (
          externalSignal &&
          externalAbortHandler
        ) {

          externalSignal.removeEventListener(
            "abort",
            externalAbortHandler
          );

        }

      }

    };


  // =====================================================
  // CSRF TOKEN
  // =====================================================

  const getCsrfToken =
    async (
      forceRefresh = false
    ) => {

      if (
        !forceRefresh &&
        csrfTokenRef.current
      ) {

        return csrfTokenRef.current;

      }


      if (
        !forceRefresh &&
        csrfPromiseRef.current
      ) {

        return csrfPromiseRef.current;

      }


      if (
        forceRefresh
      ) {

        csrfTokenRef.current =
          null;

      }


      const request =
        (async () => {

          try {

            console.log(
              "GETTING CSRF TOKEN...",
              forceRefresh
                ? "(FORCE REFRESH)"
                : ""
            );


            /*
             * IMPORTANT:
             *
             * No Cache-Control request header.
             *
             * It can unnecessarily trigger CORS
             * preflight.
             */

            const response =
              await fetchWithTimeout(
                `${API_BASE}/auth/csrf`,
                {
                  method: "GET",

                  credentials:
                    "include",

                  cache:
                    "no-store",

                  headers: {
                    Accept:
                      "application/json"
                  }
                },
                10000
              );


            console.log(
              "CSRF RESPONSE:",
              response.status
            );


            const data =
              await parseResponseData(
                response
              );


            if (
              !response.ok
            ) {

              const error =
                new Error(
                  data.detail ||
                  data.message ||
                  "Unable to get CSRF token"
                );

              error.status =
                response.status;

              throw error;

            }


            if (
              !data.csrf_token
            ) {

              throw new Error(
                "CSRF token not received"
              );

            }


            csrfTokenRef.current =
              data.csrf_token;


            console.log(
              "CSRF TOKEN READY"
            );


            return data.csrf_token;

          } catch (error) {

            console.error(
              "CSRF TOKEN ERROR:",
              error
            );

            throw error;

          }

        })();


      csrfPromiseRef.current =
        request;


      try {

        return await request;

      } finally {

        if (
          csrfPromiseRef.current ===
          request
        ) {

          csrfPromiseRef.current =
            null;

        }

      }

    };


  // =====================================================
  // REFRESH ACCESS TOKEN
  // =====================================================

  const refreshAccessToken =
    async () => {

      if (
        refreshPromiseRef.current
      ) {

        return refreshPromiseRef.current;

      }


      const request =
        (async () => {

          try {

            console.log(
              "ACCESS TOKEN MAY BE EXPIRED. REFRESHING..."
            );


            // =================================================
            // FIRST REFRESH REQUEST
            // =================================================

            let response =
              await fetchWithTimeout(
                `${API_BASE}/auth/refresh`,
                {
                  method: "POST",

                  credentials:
                    "include",

                  cache:
                    "no-store",

                  headers: {
                    Accept:
                      "application/json"
                  }
                },
                10000
              );


            let data =
              await parseResponseData(
                response
              );


            console.log(
              "REFRESH RESPONSE:",
              response.status,
              data
            );


            // =================================================
            // SUCCESS
            // =================================================

            if (
              response.ok
            ) {

              console.log(
                "ACCESS TOKEN REFRESHED SUCCESSFULLY"
              );

              return "success";

            }


            // =================================================
            // CSRF REQUIRED
            // =================================================

            if (
              response.status ===
              403
            ) {

              console.log(
                "REFRESH returned 403. Trying with fresh CSRF..."
              );


              try {

                const csrfToken =
                  await getCsrfToken(
                    true
                  );


                response =
                  await fetchWithTimeout(
                    `${API_BASE}/auth/refresh`,
                    {
                      method: "POST",

                      credentials:
                        "include",

                      cache:
                        "no-store",

                      headers: {
                        Accept:
                          "application/json",

                        "X-CSRF-Token":
                          csrfToken
                      }
                    },
                    10000
                  );


                data =
                  await parseResponseData(
                    response
                  );


                console.log(
                  "REFRESH WITH CSRF RESPONSE:",
                  response.status,
                  data
                );


                if (
                  response.ok
                ) {

                  console.log(
                    "ACCESS TOKEN REFRESHED SUCCESSFULLY"
                  );

                  return "success";

                }

              } catch (
                csrfError
              ) {

                console.warn(
                  "CSRF refresh during token refresh failed:",
                  csrfError
                );

              }

            }


            // =================================================
            // DEFINITIVE AUTH FAILURE
            // =================================================

            if (
              response.status ===
                401 ||
              response.status ===
                403
            ) {

              console.warn(
                "REFRESH TOKEN IS INVALID OR EXPIRED."
              );

              return "auth_failed";

            }


            // =================================================
            // SERVER ERROR
            // =================================================

            if (
              response.status >=
              500
            ) {

              console.warn(
                "Refresh server error:",
                response.status
              );

              return "server_error";

            }


            return "server_error";

          } catch (error) {

            console.warn(
              "TOKEN REFRESH NETWORK ERROR:",
              error
            );

            /*
             * NEVER logout just because the network
             * temporarily failed.
             */

            return "network_error";

          }

        })();


      refreshPromiseRef.current =
        request;


      try {

        return await request;

      } finally {

        if (
          refreshPromiseRef.current ===
          request
        ) {

          refreshPromiseRef.current =
            null;

        }

      }

    };


  // =====================================================
  // FINAL AUTH EXPIRY
  // =====================================================

  const handleAuthExpired =
    () => {

      console.warn(
        "Authentication permanently expired."
      );


      csrfTokenRef.current =
        null;

      csrfPromiseRef.current =
        null;

      refreshPromiseRef.current =
        null;


      setFiles([]);


      navigate(
        "/login",
        {
          replace: true,

          state: {
            from:
              location.pathname
          }
        }
      );

    };


  // =====================================================
  // AUTHENTICATED REQUEST
  //
  // Used for GET requests.
  //
  // IMPORTANT:
  // GET requests do NOT send CSRF header.
  // =====================================================

  const fetchAuthenticated =
    async (
      url,
      options = {},
      timeout = 30000
    ) => {

      let response;


      // =================================================
      // FIRST REQUEST
      // =================================================

      try {

        response =
          await fetchWithTimeout(
            url,
            {
              ...options,

              credentials:
                "include",

              cache:
                "no-store",

              headers: {
                ...(options.headers || {}),

                Accept:
                  options.headers?.Accept ||
                  "application/json"
              }
            },
            timeout
          );

      } catch (error) {

        console.error(
          "AUTHENTICATED REQUEST NETWORK ERROR:",
          error
        );

        throw error;

      }


      console.log(
        "AUTHENTICATED RESPONSE:",
        response.status,
        url
      );


      // =================================================
      // NOT 401
      // =================================================

      if (
        response.status !==
        401
      ) {

        return response;

      }


      // =================================================
      // 401
      // REFRESH ACCESS TOKEN
      // =================================================

      console.warn(
        "401 received. Attempting access-token refresh..."
      );


      const refreshResult =
        await refreshAccessToken();


      // =================================================
      // REFRESH SUCCESS
      // =================================================

      if (
        refreshResult ===
        "success"
      ) {

        console.log(
          "Retrying original authenticated request..."
        );


        response =
          await fetchWithTimeout(
            url,
            {
              ...options,

              credentials:
                "include",

              cache:
                "no-store",

              headers: {
                ...(options.headers || {}),

                Accept:
                  options.headers?.Accept ||
                  "application/json"
              }
            },
            timeout
          );


        console.log(
          "AUTHENTICATED RETRY RESPONSE:",
          response.status,
          url
        );


        return response;

      }


      // =================================================
      // AUTH FAILURE
      // =================================================

      if (
        refreshResult ===
        "auth_failed"
      ) {

        handleAuthExpired();

        return response;

      }


      // =================================================
      // NETWORK / SERVER ERROR
      // =================================================

      throw new Error(
        refreshResult ===
        "network_error"
          ? "Connection to server was lost. Please try again."
          : "Authentication server is temporarily unavailable. Please try again."
      );

    };


  // =====================================================
  // FETCH WITH CSRF
  // =====================================================

  const fetchWithCsrf =
    async (
      url,
      options = {}
    ) => {

      let csrfToken =
        await getCsrfToken();


      const makeRequest =
        async token => {

          return fetchWithTimeout(
            url,
            {
              ...options,

              credentials:
                "include",

              cache:
                "no-store",

              headers: {
                ...(options.headers || {}),

                Accept:
                  "application/json",

                "X-CSRF-Token":
                  token
              }
            },
            30000
          );

        };


      // =================================================
      // FIRST REQUEST
      // =================================================

      let response =
        await makeRequest(
          csrfToken
        );


      // =================================================
      // SUCCESS
      // =================================================

      if (
        response.status !==
          401 &&
        response.status !==
          403
      ) {

        return response;

      }


      // =================================================
      // CSRF ERROR
      // =================================================

      if (
        response.status ===
        403
      ) {

        console.warn(
          "403 received. Refreshing CSRF..."
        );


        csrfToken =
          await getCsrfToken(
            true
          );


        response =
          await makeRequest(
            csrfToken
          );


        if (
          response.status !==
          401
        ) {

          return response;

        }

      }


      // =================================================
      // ACCESS TOKEN ERROR
      // =================================================

      if (
        response.status ===
        401
      ) {

        console.warn(
          "401 received. Refreshing authentication..."
        );


        const refreshResult =
          await refreshAccessToken();


        // =================================================
        // REFRESH SUCCESS
        // =================================================

        if (
          refreshResult ===
          "success"
        ) {

          console.log(
            "Authentication refreshed. Getting new CSRF..."
          );


          csrfToken =
            await getCsrfToken(
              true
            );


          response =
            await makeRequest(
              csrfToken
            );


          if (
            response.status ===
            403
          ) {

            csrfToken =
              await getCsrfToken(
                true
              );


            response =
              await makeRequest(
                csrfToken
              );

          }


          return response;

        }


        // =================================================
        // AUTH FAILURE
        // =================================================

        if (
          refreshResult ===
          "auth_failed"
        ) {

          handleAuthExpired();

          return response;

        }


        // =================================================
        // SERVER / NETWORK
        // =================================================

        throw new Error(
          refreshResult ===
          "network_error"
            ? "Connection to server was lost. Please try again."
            : "Authentication server is temporarily unavailable. Please try again."
        );

      }


      return response;

    };


  // =====================================================
  // CSRF REQUEST WITH NETWORK RETRY
  // =====================================================

  const fetchWithCsrfRetry =
    async (
      url,
      options = {},
      maxRetries = 2
    ) => {

      let lastError =
        null;


      for (
        let attempt = 0;
        attempt <= maxRetries;
        attempt++
      ) {

        try {

          const response =
            await fetchWithCsrf(
              url,
              options
            );


          return response;

        } catch (error) {

          lastError =
            error;


          console.warn(
            `Request failed. Attempt ${attempt + 1}/${maxRetries + 1}`,
            error
          );


          if (
            attempt <
            maxRetries
          ) {

            await new Promise(
              resolve =>
                setTimeout(
                  resolve,
                  1000 *
                  (attempt + 1)
                )
            );

          }

        }

      }


      throw (
        lastError ||
        new Error(
          "Network connection lost."
        )
      );

    };


  // =====================================================
  // LOAD CONTENTS
  //
  // MAIN FIX
  //
  // No Cache-Control request header.
  //
  // For GET:
  //
  // credentials: include
  // cache: no-store
  // Accept: application/json
  //
  // No X-CSRF-Token.
  //
  // This avoids unnecessary CORS preflight.
  // =====================================================

  const loadContents =
    async (
      folderReference = null
    ) => {

      const requestId =
        ++contentsRequestRef.current;


      try {

        setLoading(true);
        setOpenMenu(null);


        // =================================================
        // RESET ROOT
        // =================================================

        if (
          folderReference === null
        ) {

          setCurrentFolder(null);

          setCurrentFolderPublicId(null);

          setCurrentFolderName(null);

          setFolderPath([]);

        }


        // =================================================
        // BUILD URL
        // =================================================

        const url =
          folderReference === null
            ? `${API_BASE}/storage/contents`
            : `${API_BASE}/storage/contents?folder_id=${encodeURIComponent(
                folderReference
              )}`;


        console.log(
          "================================="
        );

        console.log(
          "LOADING STORAGE CONTENTS"
        );

        console.log(
          "REQUEST ID:",
          requestId
        );

        console.log(
          "PATH:",
          location.pathname
        );

        console.log(
          "FOLDER:",
          folderReference
        );

        console.log(
          "URL:",
          url
        );

        console.log(
          "BEFORE GET /storage/contents"
        );

        console.log(
          "================================="
        );


        // =================================================
        // GET REQUEST
        // =================================================

        const response =
          await fetchAuthenticated(
            url,
            {
              method: "GET",

              credentials:
                "include",

              cache:
                "no-store",

              /*
               * IMPORTANT:
               *
               * DO NOT add:
               *
               * "Cache-Control": "no-cache"
               *
               * because that can trigger unnecessary
               * CORS preflight.
               */

              headers: {
                Accept:
                  "application/json"
              }
            },
            30000
          );


        console.log(
          "AFTER GET /storage/contents:",
          response.status
        );


        const data =
          await parseResponseData(
            response
          );


        // =================================================
        // IGNORE OLD REQUEST
        // =================================================

        if (
          requestId !==
          contentsRequestRef.current
        ) {

          console.log(
            "Ignoring old contents response:",
            requestId
          );

          return;

        }


        // =================================================
        // AUTH ERROR
        // =================================================

        if (
          response.status ===
          401
        ) {

          return;

        }


        // =================================================
        // SERVER ERROR
        // =================================================

        if (
          !response.ok
        ) {

          console.error(
            "Contents loading failed:",
            response.status,
            data
          );


          throw new Error(
            data.detail ||
            data.message ||
            `Unable to load storage (${response.status})`
          );

        }


        console.log(
          "SERVER CONTENTS:",
          data
        );


        // =================================================
        // ROOT
        // =================================================

        if (
          folderReference === null
        ) {

          setCurrentFolder(null);

          setCurrentFolderPublicId(null);

          setCurrentFolderName(null);

          setFolderPath([]);

        }


        // =================================================
        // CURRENT FOLDER
        // =================================================

        if (
          folderReference !== null
        ) {

          const internalId =
            data.folder_id ??
            data.current_folder?.id ??
            data.folder?.id ??
            data.currentFolder?.id ??
            null;


          if (
            internalId !== null &&
            internalId !== undefined
          ) {

            setCurrentFolder(
              Number(internalId)
            );

          }


          const publicId =
            data.folder_public_id ??
            data.current_folder?.public_id ??
            data.current_folder?.publicId ??
            data.folder?.public_id ??
            data.folder?.publicId ??
            data.currentFolder?.public_id ??
            data.currentFolder?.publicId ??
            folderReference;


          setCurrentFolderPublicId(
            publicId
          );


          const folderName =
            data.folder_name ??
            data.current_folder?.name ??
            data.folder?.name ??
            data.currentFolder?.name ??
            null;


          setCurrentFolderName(
            folderName
          );


          // =================================================
          // BREADCRUMBS
          // =================================================

          if (
            Array.isArray(
              data.breadcrumbs
            )
          ) {

            const breadcrumbs =
              data.breadcrumbs
                .map(
                  folder => ({

                    id:
                      folder.id,

                    publicId:
                      folder.public_id ??
                      folder.publicId ??
                      null,

                    name:
                      folder.name

                  })
                )
                .filter(
                  folder =>
                    folder.publicId
                );


            setFolderPath(
              breadcrumbs
            );

          } else if (
            publicId &&
            folderName
          ) {

            setFolderPath([
              {
                id:
                  internalId,

                publicId:
                  publicId,

                name:
                  folderName
              }
            ]);

          } else {

            setFolderPath([]);

          }

        }


        // =================================================
        // FOLDERS
        // =================================================

        const folders =
          Array.isArray(
            data.folders
          )
            ? data.folders.map(
                folder => ({

                  id:
                    folder.id,

                  publicId:
                    folder.public_id ??
                    folder.publicId ??
                    null,

                  name:
                    folder.name,

                  type:
                    "folder",

                  modified:
                    folder.modified ||
                    "Just now",

                  size:
                    null,

                  parentId:
                    folder.parent_id ??
                    folder.parentId ??
                    null,

                  isShared:
                    folder.is_shared ??
                    folder.isShared ??
                    false,

                  shareId:
                    folder.share_id ??
                    folder.shareId ??
                    null,

                  sharePermission:
                    folder.share_permission ??
                    folder.sharePermission ??
                    "viewer"

                })
              )
            : [];


        // =================================================
        // FILES
        // =================================================

        const serverFiles =
          Array.isArray(
            data.files
          )
            ? data.files.map(
                file => {

                  const mimeType =
                    file.mime_type ??
                    file.mimeType ??
                    "";


                  const detectedType =
                    getFileType(
                      file.name
                    );


                  let finalType =
                    detectedType;


                  if (
                    mimeType.startsWith(
                      "video/"
                    )
                  ) {

                    finalType =
                      "video";

                  }


                  if (
                    file.type ===
                    "video"
                  ) {

                    finalType =
                      "video";

                  }


                  return {

                    id:
                      file.id,

                    name:
                      file.name,

                    type:
                      finalType,

                    modified:
                      file.modified ||
                      "Just now",

                    size:
                      formatFileSize(
                        Number(
                          file.size ||
                          0
                        )
                      ),

                    parentId:
                      file.folder_id ??
                      file.folderId ??
                      file.parent_id ??
                      null,

                    mimeType:
                      mimeType ||
                      null,

                    storedName:
                      file.stored_name ??
                      file.storedName ??
                      null,

                    isShared:
                      file.is_shared ??
                      file.isShared ??
                      false,

                    shareId:
                      file.share_id ??
                      file.shareId ??
                      null,

                    sharePermission:
                      file.share_permission ??
                      file.sharePermission ??
                      "viewer"

                  };

                }
              )
            : [];


        // =================================================
        // FINAL CONTENT
        // =================================================

        setFiles([
          ...folders,
          ...serverFiles
        ]);


      } catch (error) {

        // =================================================
        // IGNORE STALE REQUEST
        // =================================================

        if (
          requestId !==
          contentsRequestRef.current
        ) {

          return;

        }


        console.error(
          "Load contents error:",
          error
        );


        // =================================================
        // TIMEOUT
        // =================================================

        if (
          error?.name ===
          "AbortError"
        ) {

          console.warn(
            "Contents request timed out."
          );


          alert(
            "Server took too long to respond. Please try again."
          );


          return;

        }


        alert(
          error.message ||
          "Cannot connect to server."
        );


      } finally {

        if (
          requestId ===
          contentsRequestRef.current
        ) {

          setLoading(false);

        }

      }

    };


  // =====================================================
  // STORAGE PAGE ENTRY
  // =====================================================

  useEffect(() => {

    console.log(
      "================================="
    );

    console.log(
      "STORAGE PAGE ENTERED"
    );

    console.log(
      "PATH:",
      location.pathname
    );

    console.log(
      "FOLDER ID:",
      urlFolderId
    );

    console.log(
      "================================="
    );


    setFiles([]);

    setLoading(true);

    setOpenMenu(null);

    setSelectedVideo(null);

    setShareModal(null);

    setShareData(null);

    setShareLoading(false);

    setShowNewMenu(false);

    setShowTypeMenu(false);

    setShowModifiedMenu(false);


    loadContents(
      urlFolderId || null
    );

  }, [
    location.pathname,
    urlFolderId
  ]);


  // =====================================================
  // FILTERED ITEMS
  // =====================================================

  const currentItems =
    useMemo(() => {

      let result =
        [...files];


      if (
        search.trim()
      ) {

        const searchText =
          search
            .trim()
            .toLowerCase();


        result =
          result.filter(
            item =>
              String(
                item.name || ""
              )
                .toLowerCase()
                .includes(
                  searchText
                )
          );

      }


      if (
        typeFilter !==
        "all"
      ) {

        result =
          result.filter(
            item =>
              item.type ===
              typeFilter
          );

      }


      if (
        modifiedFilter !==
        "all"
      ) {

        result =
          result.filter(
            item =>
              item.modified ===
              modifiedFilter
          );

      }


      return result;

    }, [
      files,
      search,
      typeFilter,
      modifiedFilter
    ]);


  // =====================================================
  // CREATE FOLDER
  // =====================================================

  const createFolder =
    async () => {

      const folderName =
        window.prompt(
          "Enter folder name"
        );


      if (
        !folderName ||
        !folderName.trim()
      ) {

        return;

      }


      const name =
        folderName.trim();


      try {

        setShowNewMenu(false);


        const response =
          await fetchWithCsrfRetry(
            `${API_BASE}/storage/folder`,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json"
              },

              body:
                JSON.stringify({

                  name:
                    name,

                  parent_id:
                    currentFolder ??
                    null

                })
            }
          );


        const data =
          await parseResponseData(
            response
          );


        console.log(
          "CREATE FOLDER:",
          response.status,
          data
        );


        if (
          !response.ok
        ) {

          alert(
            data.detail ||
            data.message ||
            "Unable to create folder."
          );

          return;

        }


        await loadContents(
          urlFolderId || null
        );


      } catch (error) {

        console.error(
          "Create folder error:",
          error
        );


        alert(
          error.message ||
          "Unable to create folder."
        );

      }

    };


  // =====================================================
  // FILE PICKER
  // =====================================================

  const openFilePicker =
    () => {

      setShowNewMenu(false);


      if (
        fileInputRef.current
      ) {

        fileInputRef.current.click();

      }

    };


  // =====================================================
  // FOLDER PICKER
  // =====================================================

  const openFolderPicker =
    () => {

      setShowNewMenu(false);


      if (
        folderInputRef.current
      ) {

        folderInputRef.current.click();

      }

    };


  // =====================================================
  // SINGLE XHR UPLOAD
  // =====================================================

  const uploadSingleFile = (
    file,
    uploadUrl,
    csrfToken,
    retryCount = 0
  ) => {

    const MAX_RETRIES =
      2;


    return new Promise(
      (
        resolve,
        reject
      ) => {

        const xhr =
          new XMLHttpRequest();


        const formData =
          new FormData();


        formData.append(
          "file",
          file
        );


        xhr.open(
          "POST",
          uploadUrl,
          true
        );


        xhr.withCredentials =
          true;


        xhr.setRequestHeader(
          "X-CSRF-Token",
          csrfToken
        );


        xhr.setRequestHeader(
          "Accept",
          "application/json"
        );


        xhr.timeout =
          30 * 60 * 1000;


        xhr.upload.onprogress =
          event => {

            if (
              event.lengthComputable
            ) {

              const percent =
                Math.round(
                  (
                    event.loaded /
                    event.total
                  ) * 100
                );


              setUploadProgress(
                percent
              );

            }

          };


        xhr.onload =
          async () => {

            let data = {};


            try {

              data =
                xhr.responseText
                  ? JSON.parse(
                      xhr.responseText
                    )
                  : {};

            } catch {

              data = {
                detail:
                  xhr.responseText
              };

            }


            if (
              xhr.status >= 200 &&
              xhr.status < 300
            ) {

              resolve(
                data
              );

              return;

            }


            const error =
              new Error(
                data.detail ||
                data.message ||
                `Upload failed (${xhr.status})`
              );


            error.status =
              xhr.status;


            reject(
              error
            );

          };


        xhr.onerror =
          () => {

            if (
              retryCount <
              MAX_RETRIES
            ) {

              console.warn(
                `Upload network error. Retrying ${retryCount + 1}/${MAX_RETRIES}...`
              );


              setUploadStatus(
                `Connection lost. Retrying ${retryCount + 1}/${MAX_RETRIES}...`
              );


              setTimeout(
                () => {

                  uploadSingleFile(
                    file,
                    uploadUrl,
                    csrfToken,
                    retryCount + 1
                  )
                    .then(
                      resolve
                    )
                    .catch(
                      reject
                    );

                },
                1500
              );


              return;

            }


            const error =
              new Error(
                "Network connection lost during upload."
              );


            error.status =
              0;


            reject(
              error
            );

          };


        xhr.ontimeout =
          () => {

            if (
              retryCount <
              MAX_RETRIES
            ) {

              console.warn(
                `Upload timeout. Retrying ${retryCount + 1}/${MAX_RETRIES}...`
              );


              setUploadStatus(
                `Upload timeout. Retrying ${retryCount + 1}/${MAX_RETRIES}...`
              );


              setTimeout(
                () => {

                  uploadSingleFile(
                    file,
                    uploadUrl,
                    csrfToken,
                    retryCount + 1
                  )
                    .then(
                      resolve
                    )
                    .catch(
                      reject
                    );

                },
                1500
              );


              return;

            }


            const error =
              new Error(
                "Upload timed out. Please check your connection."
              );


            error.status =
              408;


            reject(
              error
            );

          };


        xhr.onabort =
          () => {

            const error =
              new Error(
                "Upload cancelled."
              );


            error.status =
              499;


            reject(
              error
            );

          };


        try {

          xhr.send(
            formData
          );

        } catch (error) {

          reject(
            error
          );

        }

      }
    );

  };


  // =====================================================
  // FILE UPLOAD
  // =====================================================

  const handleFileUpload =
    async event => {

      const selectedFiles =
        Array.from(
          event.target.files ||
          []
        );


      if (
        !selectedFiles.length
      ) {

        return;

      }


      try {

        let csrfToken =
          await getCsrfToken();


        setUploading(true);

        setUploadCompleted(
          false
        );

        setUploadCurrent(0);

        setUploadTotal(
          selectedFiles.length
        );

        setUploadProgress(0);


        for (
          let index = 0;
          index <
          selectedFiles.length;
          index++
        ) {

          const file =
            selectedFiles[index];


          setUploadCurrent(
            index + 1
          );

          setUploadFileName(
            file.name
          );

          setUploadStatus(
            "Uploading..."
          );

          setUploadProgress(
            0
          );


          const uploadUrl =
            currentFolder === null ||
            currentFolder === undefined
              ? `${API_BASE}/storage/upload`
              : `${API_BASE}/storage/upload?folder_id=${encodeURIComponent(
                  currentFolder
                )}`;


          try {

            await uploadSingleFile(
              file,
              uploadUrl,
              csrfToken
            );


          } catch (error) {

            if (
              error.status ===
              403
            ) {

              console.warn(
                "UPLOAD 403. Refreshing CSRF..."
              );


              csrfToken =
                await getCsrfToken(
                  true
                );


              await uploadSingleFile(
                file,
                uploadUrl,
                csrfToken
              );


            } else if (
              error.status ===
              401
            ) {

              console.warn(
                "UPLOAD 401. Refreshing authentication..."
              );


              const refreshResult =
                await refreshAccessToken();


              if (
                refreshResult ===
                "success"
              ) {

                csrfToken =
                  await getCsrfToken(
                    true
                  );


                await uploadSingleFile(
                  file,
                  uploadUrl,
                  csrfToken
                );


              } else if (
                refreshResult ===
                "auth_failed"
              ) {

                handleAuthExpired();

                throw error;


              } else {

                throw new Error(
                  refreshResult ===
                  "network_error"
                    ? "Connection lost while refreshing login. Please try again."
                    : "Authentication server is temporarily unavailable."
                );

              }


            } else if (
              error.status ===
              0
            ) {

              throw new Error(
                "Connection lost while uploading. Please try again."
              );


            } else {

              throw error;

            }

          }

        }


        setUploadProgress(
          100
        );

        setUploadStatus(
          "Upload complete"
        );

        setUploadCompleted(
          true
        );


        await loadContents(
          urlFolderId || null
        );


        await new Promise(
          resolve =>
            setTimeout(
              resolve,
              700
            )
        );


      } catch (error) {

        console.error(
          "File upload error:",
          error
        );


        if (
          error.message !==
          "Upload cancelled."
        ) {

          alert(
            error.message ||
            "File upload failed."
          );

        }


      } finally {

        setUploading(false);

        setUploadFileName("");

        setUploadStatus("");

        setUploadProgress(0);

        setUploadCurrent(0);

        setUploadTotal(0);

        setUploadCompleted(
          false
        );


        event.target.value =
          "";

      }

    };


  // =====================================================
  // FOLDER UPLOAD
  // =====================================================

  const handleFolderUpload =
    async event => {

      const selectedFiles =
        Array.from(
          event.target.files ||
          []
        );


      if (
        !selectedFiles.length
      ) {

        return;

      }


      try {

        let csrfToken =
          await getCsrfToken();


        setUploading(true);

        setUploadCompleted(
          false
        );

        setUploadCurrent(0);

        setUploadTotal(
          selectedFiles.length
        );

        setUploadProgress(0);


        const folderMap =
          new Map();


        for (
          let fileIndex = 0;
          fileIndex <
          selectedFiles.length;
          fileIndex++
        ) {

          const file =
            selectedFiles[fileIndex];


          setUploadCurrent(
            fileIndex + 1
          );

          setUploadFileName(
            file.name
          );


          const relativePath =
            file.webkitRelativePath ||
            file.name;


          const parts =
            relativePath.split(
              "/"
            );


          const folderParts =
            parts.slice(
              0,
              -1
            );


          let parentId =
            currentFolder ??
            null;


          // =================================================
          // CREATE / FIND FOLDERS
          // =================================================

          for (
            let i = 0;
            i <
            folderParts.length;
            i++
          ) {

            const folderName =
              folderParts[i];


            const pathKey = [
              currentFolder ??
                "root",
              ...folderParts.slice(
                0,
                i + 1
              )
            ].join("/");


            if (
              folderMap.has(
                pathKey
              )
            ) {

              parentId =
                folderMap.get(
                  pathKey
                );

              continue;

            }


            setUploadStatus(
              `Creating folder: ${folderName}`
            );


            const folderResponse =
              await fetchWithCsrfRetry(
                `${API_BASE}/storage/folder`,
                {
                  method: "POST",

                  headers: {
                    "Content-Type":
                      "application/json"
                  },

                  body:
                    JSON.stringify({

                      name:
                        folderName,

                      parent_id:
                        parentId

                    })
                }
              );


            const folderData =
              await parseResponseData(
                folderResponse
              );


            // =================================================
            // EXISTING FOLDER
            // =================================================

            if (
              folderResponse.status ===
              409
            ) {

              const existingUrl =
                parentId === null
                  ? `${API_BASE}/storage/contents`
                  : `${API_BASE}/storage/contents?folder_id=${encodeURIComponent(
                      parentId
                    )}`;


              const existingResponse =
                await fetchAuthenticated(
                  existingUrl,
                  {
                    method: "GET",

                    credentials:
                      "include",

                    cache:
                      "no-store",

                    headers: {
                      Accept:
                        "application/json"
                    }
                  },
                  30000
                );


              const existingData =
                await parseResponseData(
                  existingResponse
                );


              if (
                existingResponse.status ===
                401
              ) {

                throw new Error(
                  "Authentication expired."
                );

              }


              if (
                !existingResponse.ok
              ) {

                throw new Error(
                  existingData.detail ||
                  existingData.message ||
                  `Unable to find existing folder "${folderName}"`
                );

              }


              const existingFolder =
                (
                  existingData.folders ||
                  []
                ).find(
                  folder =>
                    folder.name ===
                    folderName
                );


              if (
                !existingFolder
              ) {

                throw new Error(
                  `Cannot find existing folder "${folderName}"`
                );

              }


              parentId =
                existingFolder.id;


              folderMap.set(
                pathKey,
                parentId
              );


              continue;

            }


            // =================================================
            // OTHER ERROR
            // =================================================

            if (
              !folderResponse.ok
            ) {

              throw new Error(
                folderData.detail ||
                folderData.message ||
                `Failed to create folder "${folderName}"`
              );

            }


            // =================================================
            // NEW FOLDER ID
            // =================================================

            parentId =
              folderData.id;


            if (
              parentId ===
                undefined ||
              parentId ===
                null
            ) {

              throw new Error(
                `Folder "${folderName}" was created but server did not return its ID.`
              );

            }


            folderMap.set(
              pathKey,
              parentId
            );

          }


          // =================================================
          // UPLOAD FILE
          // =================================================

          setUploadStatus(
            "Uploading..."
          );

          setUploadProgress(
            0
          );


          const uploadUrl =
            parentId === null
              ? `${API_BASE}/storage/upload`
              : `${API_BASE}/storage/upload?folder_id=${encodeURIComponent(
                  parentId
                )}`;


          try {

            await uploadSingleFile(
              file,
              uploadUrl,
              csrfToken
            );


          } catch (error) {

            if (
              error.status ===
              403
            ) {

              console.warn(
                "FOLDER UPLOAD 403. Refreshing CSRF..."
              );


              csrfToken =
                await getCsrfToken(
                  true
                );


              await uploadSingleFile(
                file,
                uploadUrl,
                csrfToken
              );


            } else if (
              error.status ===
              401
            ) {

              console.warn(
                "FOLDER UPLOAD 401. Refreshing authentication..."
              );


              const refreshResult =
                await refreshAccessToken();


              if (
                refreshResult ===
                "success"
              ) {

                csrfToken =
                  await getCsrfToken(
                    true
                  );


                await uploadSingleFile(
                  file,
                  uploadUrl,
                  csrfToken
                );


              } else if (
                refreshResult ===
                "auth_failed"
              ) {

                handleAuthExpired();

                throw error;


              } else {

                throw new Error(
                  refreshResult ===
                  "network_error"
                    ? "Connection lost while refreshing login."
                    : "Authentication server is temporarily unavailable."
                );

              }


            } else if (
              error.status ===
              0
            ) {

              throw new Error(
                "Connection lost while uploading folder file."
              );


            } else {

              throw error;

            }

          }

        }


        setUploadProgress(
          100
        );

        setUploadStatus(
          "Upload complete"
        );

        setUploadCompleted(
          true
        );


        await loadContents(
          urlFolderId || null
        );


        await new Promise(
          resolve =>
            setTimeout(
              resolve,
              700
            )
        );


      } catch (error) {

        console.error(
          "Folder upload error:",
          error
        );


        alert(
          error.message ||
          "Folder upload failed."
        );


      } finally {

        setUploading(false);

        setUploadFileName("");

        setUploadStatus("");

        setUploadProgress(0);

        setUploadCurrent(0);

        setUploadTotal(0);

        setUploadCompleted(
          false
        );


        event.target.value =
          "";

      }

    };


  // =====================================================
  // OPEN FOLDER
  // =====================================================

  const openFolder =
    folder => {

      if (
        !folder?.publicId
      ) {

        console.error(
          "Folder public ID missing:",
          folder
        );


        alert(
          "Folder ID is missing."
        );


        return;

      }


      setOpenMenu(
        null
      );


      navigate(
        `/storage/folders/${folder.publicId}`
      );

    };


  // =====================================================
  // GO ROOT
  // =====================================================

  const goToRoot =
    () => {

      setOpenMenu(
        null
      );


      navigate(
        "/storage"
      );

    };


  // =====================================================
  // GO TO BREADCRUMB
  // =====================================================

  const goToFolder =
    index => {

      const selected =
        folderPath[index];


      if (
        !selected?.publicId
      ) {

        console.error(
          "Breadcrumb public ID missing:",
          selected
        );

        return;

      }


      setOpenMenu(
        null
      );


      navigate(
        `/storage/folders/${selected.publicId}`
      );

    };


  // =====================================================
  // OPEN VIDEO
  // =====================================================

  const openVideo =
    item => {

      if (
        !item?.id
      ) {

        alert(
          "Video ID is missing."
        );

        return;

      }


      setOpenMenu(
        null
      );


      setSelectedVideo({

        id:
          item.id,

        name:
          item.name,

        mimeType:
          item.mimeType ||
          "video/mp4",

        type:
          "video"

      });

    };


  // =====================================================
  // CLOSE VIDEO
  // =====================================================

  const closeVideo =
    () => {

      setSelectedVideo(
        null
      );

    };


  // =====================================================
  // CREATE SHARE
  // =====================================================

  const createShare =
    async item => {

      if (
        !item?.id
      ) {

        alert(
          "Item ID is missing."
        );

        return;

      }


      try {

        setOpenMenu(
          null
        );


        setShareModal({
          id:
            item.id,

          type:
            item.type ===
            "folder"
              ? "folder"
              : "file",

          name:
            item.name
        });


        setShareData(
          null
        );


        setShareLoading(
          true
        );


        const shareType =
          item.type ===
          "folder"
            ? "folder"
            : "file";


        const endpoint =
          shareType ===
          "folder"
            ? `${API_BASE}/share/folder/${item.id}`
            : `${API_BASE}/share/file/${item.id}`;


        const response =
          await fetchWithCsrfRetry(
            endpoint,
            {
              method: "POST",

              headers: {
                "Content-Type":
                  "application/json"
              },

              body:
                JSON.stringify({
                  permission:
                    "viewer"
                })
            }
          );


        const data =
          await parseResponseData(
            response
          );


        console.log(
          "SHARE RESPONSE:",
          response.status,
          data
        );


        if (
          !response.ok
        ) {

          alert(
            data.detail ||
            data.message ||
            "Unable to create share link."
          );


          setShareModal(
            null
          );


          return;

        }


        const shareId =
          data.share_id ??
          data.shareId ??
          data.id;


        if (
          !shareId
        ) {

          throw new Error(
            "Server did not return share ID."
          );

        }


        const shareUrl =
          `${window.location.origin}/share/${shareType}/${shareId}`;


        setShareData({

          ...data,

          share_id:
            shareId,

          shareUrl:
            shareUrl

        });


        setFiles(
          previousFiles =>
            previousFiles.map(
              currentItem => {

                if (
                  currentItem.id !==
                  item.id
                ) {

                  return currentItem;

                }


                return {

                  ...currentItem,

                  isShared:
                    true,

                  shareId:
                    shareId,

                  sharePermission:
                    "viewer"

                };

              }
            )
        );


      } catch (error) {

        console.error(
          "Create share error:",
          error
        );


        alert(
          error.message ||
          "Unable to create share link."
        );


        setShareModal(
          null
        );


      } finally {

        setShareLoading(
          false
        );

      }

    };


  // =====================================================
  // CLOSE SHARE
  // =====================================================

  const closeShareModal =
    () => {

      if (
        shareLoading
      ) {

        return;

      }


      setShareModal(
        null
      );

      setShareData(
        null
      );

    };


  // =====================================================
  // COPY SHARE LINK
  // =====================================================

  const copyShareLink =
    async () => {

      if (
        !shareData?.shareUrl
      ) {

        return;

      }


      try {

        await navigator.clipboard.writeText(
          shareData.shareUrl
        );


        alert(
          "Share link copied."
        );


      } catch (error) {

        console.error(
          "Clipboard error:",
          error
        );


        alert(
          "Unable to copy link."
        );

      }

    };


  // =====================================================
  // STOP SHARING
  // =====================================================

  const stopSharing =
    async () => {

      if (
        !shareModal?.id
      ) {

        return;

      }


      const confirmed =
        window.confirm(
          `Stop sharing "${shareModal.name}"?`
        );


      if (
        !confirmed
      ) {

        return;

      }


      try {

        setShareLoading(
          true
        );


        const endpoint =
          shareModal.type ===
          "folder"
            ? `${API_BASE}/share/folder/${shareModal.id}`
            : `${API_BASE}/share/file/${shareModal.id}`;


        const response =
          await fetchWithCsrfRetry(
            endpoint,
            {
              method: "DELETE"
            }
          );


        const data =
          await parseResponseData(
            response
          );


        if (
          !response.ok
        ) {

          alert(
            data.detail ||
            data.message ||
            "Unable to stop sharing."
          );

          return;

        }


        setFiles(
          previousFiles =>
            previousFiles.map(
              file => {

                if (
                  file.id !==
                  shareModal.id
                ) {

                  return file;

                }


                return {

                  ...file,

                  isShared:
                    false,

                  shareId:
                    null,

                  sharePermission:
                    "viewer"

                };

              }
            )
        );


        alert(
          "Sharing stopped."
        );


        setShareModal(
          null
        );


        setShareData(
          null
        );


      } catch (error) {

        console.error(
          "Stop sharing error:",
          error
        );


        alert(
          error.message ||
          "Unable to stop sharing."
        );


      } finally {

        setShareLoading(
          false
        );

      }

    };


  // =====================================================
  // ITEM CLICK
  // =====================================================

  const handleItemClick =
    item => {

      if (
        item.type ===
        "video"
      ) {

        openVideo(
          item
        );

      }

    };


  // =====================================================
  // DOWNLOAD
  // =====================================================

  const downloadItem =
    async item => {

      if (
        item.type ===
        "folder"
      ) {

        return;

      }


      try {

        const response =
          await fetchAuthenticated(
            `${API_BASE}/storage/file/${item.id}/download`,
            {
              method: "GET",

              credentials:
                "include",

              cache:
                "no-store",

              headers: {
                Accept:
                  "*/*"
              }
            },
            60000
          );


        if (
          response.status ===
          401
        ) {

          return;

        }


        if (
          !response.ok
        ) {

          const data =
            await parseResponseData(
              response
            );


          alert(
            data.detail ||
            data.message ||
            "Download failed."
          );


          return;

        }


        const blob =
          await response.blob();


        const objectUrl =
          window.URL.createObjectURL(
            blob
          );


        const anchor =
          document.createElement(
            "a"
          );


        anchor.href =
          objectUrl;


        anchor.download =
          item.name;


        document.body.appendChild(
          anchor
        );


        anchor.click();


        anchor.remove();


        window.URL.revokeObjectURL(
          objectUrl
        );


      } catch (error) {

        console.error(
          "Download error:",
          error
        );


        alert(
          error.message ||
          "Download failed."
        );


      } finally {

        setOpenMenu(
          null
        );

      }

    };


  // =====================================================
  // RENAME
  // =====================================================

  const renameItem =
    async item => {

      if (
        !item?.id
      ) {

        alert(
          "Item ID is missing."
        );

        return;

      }


      const newName =
        window.prompt(
          "Enter new name",
          item.name
        );


      if (
        !newName ||
        !newName.trim()
      ) {

        return;

      }


      const name =
        newName.trim();


      try {

        setOpenMenu(
          null
        );


        const endpoint =
          item.type ===
          "folder"
            ? `${API_BASE}/storage/folder/${item.id}`
            : `${API_BASE}/storage/file/${item.id}`;


        const response =
          await fetchWithCsrfRetry(
            endpoint,
            {
              method: "PATCH",

              headers: {
                "Content-Type":
                  "application/json"
              },

              body:
                JSON.stringify({
                  name:
                    name
                })
            },
            2
          );


        const data =
          await parseResponseData(
            response
          );


        console.log(
          "RENAME RESPONSE:",
          response.status,
          data
        );


        if (
          !response.ok
        ) {

          alert(
            data.detail ||
            data.message ||
            "Rename failed."
          );


          return;

        }


        await loadContents(
          urlFolderId || null
        );


      } catch (error) {

        console.error(
          "Rename error:",
          error
        );


        alert(
          error.message ||
          "Rename failed. Please try again."
        );

      }

    };


  // =====================================================
  // DELETE
  // =====================================================

  const deleteItem =
    async item => {

      if (
        !item?.id
      ) {

        alert(
          "Item ID is missing."
        );

        return;

      }


      const confirmed =
        window.confirm(
          `Delete "${item.name}"?`
        );


      if (
        !confirmed
      ) {

        return;

      }


      try {

        setOpenMenu(
          null
        );


        const endpoint =
          item.type ===
          "folder"
            ? `${API_BASE}/storage/folder/${item.id}`
            : `${API_BASE}/storage/file/${item.id}`;


        const response =
          await fetchWithCsrfRetry(
            endpoint,
            {
              method: "DELETE"
            }
          );


        const data =
          await parseResponseData(
            response
          );


        if (
          !response.ok
        ) {

          alert(
            data.detail ||
            data.message ||
            "Delete failed."
          );


          return;

        }


        setFiles(
          previousFiles =>
            previousFiles.filter(
              currentItem =>
                !(
                  currentItem.id ===
                  item.id &&
                  currentItem.type ===
                  item.type
                )
            )
        );


        await loadContents(
          urlFolderId || null
        );


      } catch (error) {

        console.error(
          "Delete error:",
          error
        );


        alert(
          error.message ||
          "Delete failed."
        );

      }

    };


  // =====================================================
  // FILE TYPE
  // =====================================================

  function getFileType(
    name
  ) {

    if (!name) {
      return "file";
    }


    const extension =
      name
        .split(".")
        .pop()
        .toLowerCase();


    if (
      [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
        "svg",
        "bmp",
        "ico"
      ].includes(
        extension
      )
    ) {

      return "image";

    }


    if (
      extension ===
      "pdf"
    ) {

      return "pdf";

    }


    if (
      [
        "mp4",
        "mkv",
        "mov",
        "avi",
        "webm",
        "m4v"
      ].includes(
        extension
      )
    ) {

      return "video";

    }


    if (
      [
        "mp3",
        "wav",
        "ogg",
        "m4a",
        "aac",
        "flac"
      ].includes(
        extension
      )
    ) {

      return "audio";

    }


    if (
      [
        "zip",
        "rar",
        "7z",
        "tar",
        "gz"
      ].includes(
        extension
      )
    ) {

      return "archive";

    }


    return "file";

  }


  // =====================================================
  // FORMAT SIZE
  // =====================================================

  function formatFileSize(
    bytes
  ) {

    const value =
      Number(bytes) || 0;


    if (
      value < 1024
    ) {

      return `${value} B`;

    }


    if (
      value <
      1024 * 1024
    ) {

      return `${(
        value / 1024
      ).toFixed(1)} KB`;

    }


    if (
      value <
      1024 *
      1024 *
      1024
    ) {

      return `${(
        value /
        (1024 * 1024)
      ).toFixed(1)} MB`;

    }


    return `${(
      value /
      (
        1024 *
        1024 *
        1024
      )
    ).toFixed(1)} GB`;

  }


  // =====================================================
  // ICON
  // =====================================================

  const getIcon =
    item => {

      if (
        item.type ===
        "folder"
      ) {
        return "📁";
      }


      if (
        item.type ===
        "pdf"
      ) {
        return "📕";
      }


      if (
        item.type ===
        "image"
      ) {
        return "🖼";
      }


      if (
        item.type ===
        "video"
      ) {
        return "🎬";
      }


      if (
        item.type ===
        "audio"
      ) {
        return "🎵";
      }


      if (
        item.type ===
        "archive"
      ) {
        return "🗜";
      }


      return "📄";

    };


  // =====================================================
  // STORAGE
  // =====================================================

  const usedStorage =
    "0 GB";

  const totalStorage =
    "5 GB";


  // =====================================================
  // UI
  // =====================================================

  return (

    <div className="storage-container">

      {/* =================================================
          TOP BAR
      ================================================= */}

      <div className="storage-topbar">

        <div className="storage-title">

          <h2>
            My Drive
          </h2>

          <span className="storage-title-arrow">
            ▾
          </span>

        </div>


        <div className="storage-search">

          <span>
            ⌕
          </span>


          <input
            type="text"
            placeholder="Search in Drive"
            value={search}
            onChange={e =>
              setSearch(
                e.target.value
              )
            }
          />


          {search && (

            <button
              className="search-clear"
              onClick={() =>
                setSearch("")
              }
            >
              ×
            </button>

          )}

        </div>

      </div>


      {/* =================================================
          TOOLBAR
      ================================================= */}

      <div className="storage-toolbar">

        <div className="new-wrapper">

          <button
            className="new-button"
            onClick={() =>
              setShowNewMenu(
                previous =>
                  !previous
              )
            }
          >

            <span className="new-plus">
              +
            </span>

            New

          </button>


          {showNewMenu && (

            <div className="new-menu">

              <button
                onClick={
                  createFolder
                }
              >
                <span>📁</span>
                New folder
              </button>


              <button
                onClick={
                  openFilePicker
                }
              >
                <span>📄</span>
                Upload file
              </button>


              <button
                onClick={
                  openFolderPicker
                }
              >
                <span>📂</span>
                Upload folder
              </button>

            </div>

          )}

        </div>


        {/* =================================================
            UPLOAD PANEL
        ================================================= */}

        {uploading && (

          <div className="storage-upload-panel">

            <div className="storage-upload-top">

              <div className="storage-upload-icon">

                {uploadCompleted
                  ? "✓"
                  : "↑"}

              </div>


              <div className="storage-upload-info">

                <div className="storage-upload-title">

                  {uploadCompleted
                    ? "Upload complete"
                    : uploadStatus ||
                      "Uploading..."}

                </div>


                <div className="storage-upload-file">

                  {uploadFileName ||
                    "Preparing..."}

                </div>

              </div>


              <div className="storage-upload-percent">

                {uploadProgress}%

              </div>

            </div>


            <div className="storage-upload-progress">

              <div
                className="storage-upload-progress-fill"
                style={{
                  width:
                    `${uploadProgress}%`
                }}
              />

            </div>


            <div className="storage-upload-footer">

              <span>
                {uploadCurrent} / {uploadTotal} files
              </span>


              <span>

                {uploadCompleted
                  ? "Done"
                  : "Uploading"}

              </span>

            </div>

          </div>

        )}


        {/* =================================================
            FILE INPUT
        ================================================= */}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={
            handleFileUpload
          }
        />


        {/* =================================================
            FOLDER INPUT
        ================================================= */}

        <input
          ref={folderInputRef}
          type="file"
          webkitdirectory=""
          directory=""
          multiple
          hidden
          onChange={
            handleFolderUpload
          }
        />


        {/* =================================================
            FILTERS
        ================================================= */}

        <div className="storage-filters">

          <div className="filter-wrapper">

            <button
              className="filter-button"
              onClick={() =>
                setShowTypeMenu(
                  previous =>
                    !previous
                )
              }
            >

              Type

              <span>
                ▾
              </span>

            </button>


            {showTypeMenu && (

              <div className="filter-menu">

                <button
                  onClick={() => {

                    setTypeFilter(
                      "all"
                    );

                    setShowTypeMenu(
                      false
                    );

                  }}
                >
                  All
                </button>


                <button
                  onClick={() => {

                    setTypeFilter(
                      "folder"
                    );

                    setShowTypeMenu(
                      false
                    );

                  }}
                >
                  Folders
                </button>


                <button
                  onClick={() => {

                    setTypeFilter(
                      "image"
                    );

                    setShowTypeMenu(
                      false
                    );

                  }}
                >
                  Images
                </button>


                <button
                  onClick={() => {

                    setTypeFilter(
                      "video"
                    );

                    setShowTypeMenu(
                      false
                    );

                  }}
                >
                  Videos
                </button>


                <button
                  onClick={() => {

                    setTypeFilter(
                      "audio"
                    );

                    setShowTypeMenu(
                      false
                    );

                  }}
                >
                  Audio
                </button>


                <button
                  onClick={() => {

                    setTypeFilter(
                      "pdf"
                    );

                    setShowTypeMenu(
                      false
                    );

                  }}
                >
                  PDF
                </button>

              </div>

            )}

          </div>


          <div className="filter-wrapper">

            <button
              className="filter-button"
              onClick={() =>
                setShowModifiedMenu(
                  previous =>
                    !previous
                )
              }
            >

              Modified

              <span>
                ▾
              </span>

            </button>


            {showModifiedMenu && (

              <div className="filter-menu">

                <button
                  onClick={() => {

                    setModifiedFilter(
                      "all"
                    );

                    setShowModifiedMenu(
                      false
                    );

                  }}
                >
                  Any time
                </button>


                <button
                  onClick={() => {

                    setModifiedFilter(
                      "Today"
                    );

                    setShowModifiedMenu(
                      false
                    );

                  }}
                >
                  Today
                </button>


                <button
                  onClick={() => {

                    setModifiedFilter(
                      "Yesterday"
                    );

                    setShowModifiedMenu(
                      false
                    );

                  }}
                >
                  Yesterday
                </button>


                <button
                  onClick={() => {

                    setModifiedFilter(
                      "2 days ago"
                    );

                    setShowModifiedMenu(
                      false
                    );

                  }}
                >
                  Last few days
                </button>

              </div>

            )}

          </div>

        </div>


        {/* =================================================
            VIEW SWITCH
        ================================================= */}

        <div className="view-switch">

          <button
            className={
              viewMode ===
              "list"
                ? "active"
                : ""
            }
            onClick={() =>
              setViewMode(
                "list"
              )
            }
            title="List view"
          >
            ☰
          </button>


          <button
            className={
              viewMode ===
              "grid"
                ? "active"
                : ""
            }
            onClick={() =>
              setViewMode(
                "grid"
              )
            }
            title="Grid view"
          >
            ▦
          </button>

        </div>

      </div>


      {/* =================================================
          BREADCRUMB
      ================================================= */}

      <div className="storage-breadcrumb">

        <button
          onClick={
            goToRoot
          }
          className={
            !urlFolderId
              ? "current"
              : ""
          }
        >
          My Drive
        </button>


        {folderPath.map(
          (
            folder,
            index
          ) => (

            <span
              key={
                folder.publicId ||
                folder.id
              }
              className="breadcrumb-item"
            >

              <span>
                /
              </span>


              <button
                onClick={() =>
                  goToFolder(
                    index
                  )
                }
                className={
                  index ===
                  folderPath.length - 1
                    ? "current"
                    : ""
                }
              >
                {folder.name}
              </button>

            </span>

          )
        )}

      </div>


      {/* =================================================
          FILE HEADER
      ================================================= */}

      <div className="storage-file-header">

        <span>
          Name
        </span>

        <span>
          Modified
        </span>

        <span>
          Size
        </span>

        <span></span>

      </div>


      {/* =================================================
          CONTENT
      ================================================= */}

      {loading ? (

        <StorageSkeleton
          viewMode={
            viewMode
          }
        />

      ) : currentItems.length === 0 ? (

        <div className="storage-empty">

          <div className="storage-empty-icon">
            ✦
          </div>


          <h3>

            {search
              ? "No files found"
              : "Your Drive is empty"}

          </h3>


          <p>

            {search
              ? "Try a different search."
              : "Create a folder or upload a file to get started."}

          </p>

        </div>

      ) : (

        <div
          className={
            viewMode ===
            "grid"
              ? "storage-items grid-view storage-items-loaded"
              : "storage-items list-view storage-items-loaded"
          }
        >

          {currentItems.map(
            item => (

              <div
                key={`${item.type}-${item.id}`}
                className={`storage-item ${
                  item.type ===
                  "video"
                    ? "video-file-item"
                    : ""
                }`}

                onClick={() => {

                  if (
                    item.type ===
                    "video"
                  ) {

                    handleItemClick(
                      item
                    );

                  }

                }}

                onDoubleClick={() => {

                  if (
                    item.type ===
                    "folder"
                  ) {

                    openFolder(
                      item
                    );

                  }

                }}
              >

                <div className="storage-item-icon">

                  {getIcon(
                    item
                  )}

                </div>


                <div className="storage-item-info">

                  <span className="storage-item-name">

                    {item.name}


                    {item.isShared && (

                      <span
                        title="Shared"
                        style={{
                          marginLeft:
                            "7px",

                          fontSize:
                            "13px"
                        }}
                      >
                        🔗
                      </span>

                    )}

                  </span>


                  <span className="storage-item-meta">

                    {item.modified}

                    {item.size &&
                      ` • ${item.size}`}

                  </span>

                </div>


                <span className="storage-item-modified">

                  {item.modified}

                </span>


                <span className="storage-item-size">

                  {item.size ||
                    "—"}

                </span>


                <div className="item-menu-wrapper">

                  <button
                    className="item-menu-button"

                    onClick={e => {

                      e.stopPropagation();


                      setOpenMenu(
                        previous =>
                          previous ===
                          item.id
                            ? null
                            : item.id
                      );

                    }}
                  >
                    ⋮
                  </button>


                  {openMenu ===
                    item.id && (

                    <div
                      className="item-menu"

                      onClick={e =>
                        e.stopPropagation()
                      }
                    >

                      {item.type ===
                        "folder" && (

                        <button
                          onClick={() =>
                            openFolder(
                              item
                            )
                          }
                        >
                          📂 Open
                        </button>

                      )}


                      {item.type ===
                        "video" && (

                        <button
                          onClick={() =>
                            openVideo(
                              item
                            )
                          }
                        >
                          ▶ Preview
                        </button>

                      )}


                      {item.type !==
                        "folder" && (

                        <button
                          onClick={() =>
                            downloadItem(
                              item
                            )
                          }
                        >
                          ↓ Download
                        </button>

                      )}


                      <button
                        onClick={() => {

                          setOpenMenu(
                            null
                          );

                          createShare(
                            item
                          );

                        }}
                      >
                        🔗 Share
                      </button>


                      <button
                        onClick={() =>
                          renameItem(
                            item
                          )
                        }
                      >
                        ✎ Rename
                      </button>


                      <button
                        className="danger"
                        onClick={() =>
                          deleteItem(
                            item
                          )
                        }
                      >
                        🗑 Delete
                      </button>

                    </div>

                  )}

                </div>

              </div>

            )
          )}

        </div>

      )}


      {/* =================================================
          STORAGE USAGE
      ================================================= */}

      <div className="storage-usage">

        <div className="storage-usage-bar">

          <div className="storage-usage-progress"></div>

        </div>


        <p>

          {usedStorage} of{" "}

          {totalStorage} used

        </p>

      </div>


      {/* =================================================
          VIDEO PLAYER
      ================================================= */}

      <VideoPlayer
        video={
          selectedVideo
        }
        onClose={
          closeVideo
        }
      />


      {/* =================================================
          SHARE MODAL
      ================================================= */}

      {shareModal && (

        <div
          className="share-modal-backdrop"
          onClick={
            closeShareModal
          }
        >

          <div
            className="share-modal"

            onClick={e =>
              e.stopPropagation()
            }
          >

            <div className="share-modal-header">

              <div>

                <h3>
                  Share "{shareModal.name}"
                </h3>

                <p>
                  Anyone with the link can view
                </p>

              </div>


              <button
                className="share-close-button"
                onClick={
                  closeShareModal
                }
                title="Close"
                type="button"
              >
                ×
              </button>

            </div>


            <div className="share-modal-body">

              {shareLoading ? (

                <div className="share-loading">

                  <div className="share-spinner" />

                  <span>
                    Preparing share link...
                  </span>

                </div>

              ) : shareData ? (

                <>

                  <div className="share-access-row">

                    <div className="share-access-icon">
                      🔗
                    </div>


                    <div className="share-access-info">

                      <strong>
                        Anyone with the link
                      </strong>

                      <span>
                        Viewer
                      </span>

                    </div>

                  </div>


                  <div className="share-link-box">

                    <input
                      type="text"
                      value={
                        shareData.shareUrl
                      }
                      readOnly
                      onFocus={e =>
                        e.target.select()
                      }
                    />


                    <button
                      type="button"
                      onClick={
                        copyShareLink
                      }
                    >
                      Copy link
                    </button>

                  </div>


                  <div className="share-warning">

                    Anyone with this link can
                    access this{" "}

                    {shareModal.type ===
                    "folder"
                      ? "folder and its contents"
                      : "file"}.

                  </div>

                </>

              ) : null}

            </div>


            {!shareLoading &&
              shareData && (

                <div className="share-modal-footer">

                  <button
                    type="button"
                    className="share-stop-button"
                    onClick={
                      stopSharing
                    }
                  >
                    Stop sharing
                  </button>


                  <button
                    type="button"
                    className="share-done-button"
                    onClick={
                      closeShareModal
                    }
                  >
                    Done
                  </button>

                </div>

              )}

          </div>

        </div>

      )}

    </div>

  );

}


// =========================================================
// STORAGE SKELETON
// =========================================================

function StorageSkeleton({
  viewMode
}) {

  const skeletonItems =
    Array.from({
      length:
        viewMode ===
        "grid"
          ? 10
          : 7
    });


  return (

    <div
      className={
        viewMode ===
        "grid"
          ? "storage-items grid-view storage-skeleton-container"
          : "storage-items list-view storage-skeleton-container"
      }
    >

      {skeletonItems.map(
        (
          _,
          index
        ) => (

          <div
            key={index}
            className="storage-skeleton-item"
          >

            <div className="skeleton-icon" />


            <div className="skeleton-info">

              <div className="skeleton-name" />

              <div className="skeleton-meta" />

            </div>


            <div className="skeleton-modified" />

            <div className="skeleton-size" />

            <div className="skeleton-menu" />

          </div>

        )
      )}

    </div>

  );

}


export default Storage;